import { Link, useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import { UserPlus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { CandidateBrowseRow } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Drawer } from '../../../components/Drawer';
import { Pager } from '../../../components/Pager';
import { pageCountOf } from '../../../components/pager-items';
import { useTablePreferences } from '../../../components/data-table/use-table-preferences';
import { EmptyState, FilteredEmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { useSourceOptions } from '../../../lib/app-settings';
import { formatNumber } from '../../../lib/format';
import { useModule, useStageLabels } from '../../../lib/modules';
import { EMPTY_SELECTION, type SelectionState } from '../../../lib/selection';
import { useCanEdit, useCanView, useIsFieldHidden } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useCustomFields } from '../../admin/api/settings-api';
import { usePoolOptions } from '../../pools/api/pools-api';
import { useCandidateBrowse, useCandidateFacets, useToggleStar, type BrowsePage } from '../api/candidate-browse-api';
import { useDuplicates } from '../api/candidates-api';
import {
  PAGE_SIZE,
  activeFilterCount,
  applyFilters,
  clearFilters,
  filtersOf,
  listOf,
  pickOnlyStage,
  toBrowseParams,
  toFacetParams,
  withOpen,
  withoutOpen,
  withPage,
  withSort,
  type CandidateBrowseSearch,
} from '../components/list/browse-address';
import { BulkBar } from '../components/list/BulkBar';
import { CandidateCards } from '../components/list/CandidateCards';
import { candidateColumns } from '../components/list/candidate-columns';
import { CandidateTable } from '../components/list/CandidateTable';
import { FilterPanel } from '../components/list/FilterPanel';
import { filterChips } from '../components/list/list-format';
import { listPosition, stepFrom, type NavStep } from '../components/list/panel-nav';
import { ResultsToolbar } from '../components/list/ResultsToolbar';
import type { RowHandlers } from '../components/list/RowBits';
import { StageTiles } from '../components/list/StageTiles';
import { useListLayout } from '../components/list/use-list-layout';
import { CandidatePopup, type CandidatePanelNav, type CandidatePanelTab } from '../components/panel/CandidatePanel';
import styles from './CandidatesPage.module.css';

/** Column choice and saved columns, per viewer (06-qol §3). */
const SCREEN_ID = 'candidate-browse';

/**
 * CAND-01 — All candidates, with POOL-04's Advanced search folded in (Redesign Group 1, mockup
 * Main.dc.html). Everyone who has applied or been added: the stage tiles, every filter with its
 * counts, the table or cards, and the pop-up. The whole state lives in the address (06-qol §6).
 */
export function CandidatesPage(): ReactNode {
  const canView = useCanView('candidate');
  if (!canView) return <ForbiddenState resource="candidates" contact="Your admin" />;
  return <CandidateList />;
}

function CandidateList(): ReactNode {
  const canEdit = useCanEdit('candidate');
  const canSeeSalary = !useIsFieldHidden('candidate.expectedSalary');
  const stageLabels = useStageLabels();
  const isPoolsOn = useModule('pools');
  const navigate = useNavigate();
  const router = useRouter();
  const search = useSearch({ from: '/app-layout/candidates' });
  const show = useToasts((state) => state.show);
  const [skillSearch, setSkillSearch] = useState('');
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [layout, setLayout] = useListLayout();
  // Only a pop-up opened from this list closes with Back; one opened from a link just drops `open`.
  const [isOpenedHere, setIsOpenedHere] = useState(false);

  // A shared link may carry "Over budget"; the server refuses it to someone salaries are hidden from (#5).
  const asked = canSeeSalary ? search : { ...search, overBudget: undefined };
  const browse = useCandidateBrowse(toBrowseParams(asked));
  const facets = useCandidateFacets(toFacetParams(asked, skillSearch));
  const duplicates = useDuplicates();
  const toggleStar = useToggleStar();
  const sources = useSourceOptions('filter');
  const pools = usePoolOptions(isPoolsOn);
  const customFields = useCustomFields('candidate');

  const rows = browse.data?.rows ?? [];
  const total = browse.data?.total;
  const page = search.page ?? 1;
  const pageCount = pageCountOf(total ?? 0, PAGE_SIZE);
  const filters = filtersOf(search);
  const filterCount = activeFilterCount(search);
  const openId = search.open;
  // Closed some other way (the browser's Back): the next close must not step back again.
  if (openId === undefined && isOpenedHere) setIsOpenedHere(false);

  const go = (next: CandidateBrowseSearch, replace = true): void => {
    void navigate({ to: '/candidates', search: next, replace });
  };
  /** A filter changed: what was ticked belonged to the old list. */
  const changeFilters = (next: CandidateBrowseSearch): void => {
    setSelection(EMPTY_SELECTION);
    go(next);
  };

  // --- The pop-up: open person and tab in the address; Previous / Next walk this list ----------------

  const openIndex = search.open === undefined ? -1 : rows.findIndex((row) => row.candidate.id === search.open);
  const previousStep = openIndex < 0 ? null : stepFrom(-1, openIndex, rows.length, page, pageCount);
  const nextStep = openIndex < 0 ? null : stepFrom(1, openIndex, rows.length, page, pageCount);
  // At the first or last row the neighbouring page loads ahead, so crossing over never waits. Away
  // from an edge each asks for the current page again, which the cache already holds.
  const previousPage = useCandidateBrowse(toBrowseParams(previousStep?.kind === 'page' ? withPage(search, previousStep.page) : search));
  const nextPage = useCandidateBrowse(toBrowseParams(nextStep?.kind === 'page' ? withPage(search, nextStep.page) : search));

  const openRow = (row: CandidateBrowseRow, options?: { readonly tab?: CandidatePanelTab; readonly log?: 'email' | 'call' | 'note' }): void => {
    setIsOpenedHere(true);
    go(withOpen(search, row.candidate.id, options?.tab, options?.log), false);
  };

  const closePopup = (): void => {
    if (isOpenedHere) {
      setIsOpenedHere(false);
      router.history.back();
      return;
    }
    go(withoutOpen(search));
  };

  /** A step's handler, or null at an end — and, for a moment, while the next page is still on its way. */
  const moveTo = (step: NavStep | null, ahead: typeof previousPage): (() => void) | null => {
    if (step === null) return null;
    const target = step.kind === 'row' ? rows[step.index] : pickAhead(ahead, step.at);
    if (target === undefined) return null;
    const onPage = step.kind === 'row' ? search : withPage(search, step.page);
    return () => go(withOpen(onPage, target.candidate.id, search.tab));
  };

  const nav: CandidatePanelNav | undefined =
    openIndex < 0 || total === undefined
      ? undefined
      : { position: listPosition(page, PAGE_SIZE, openIndex), total, onPrevious: moveTo(previousStep, previousPage), onNext: moveTo(nextStep, nextPage) };

  // --- Rows ----------------------------------------------------------------------------------------

  const handlers: RowHandlers = {
    onOpen: openRow,
    onToggleStar: (row) =>
      toggleStar.mutate(
        { candidateId: row.candidate.id, isStarred: !row.isStarred },
        { onError: (error) => show({ tone: 'danger', message: row.isStarred ? "The star wasn't removed" : "The star wasn't added", detail: errorMessage(error) }) },
      ),
    canEdit,
    stageLabels,
  };
  const columns = candidateColumns(handlers);
  const preferences = useTablePreferences(SCREEN_ID, columns);
  const visibleColumns = columns.filter((column) => !preferences.hiddenColumns.has(column.id));

  const chips = filterChips(search, {
    jobs: new Map((facets.data?.jobs ?? []).map((option) => [option.value, option.label])),
    tags: new Map((facets.data?.tags ?? []).map((option) => [option.value, option.label])),
    sources: new Map([...sources.map((option): [string, string] => [option.value, option.label]), ...(facets.data?.sources ?? []).map((option): [string, string] => [option.value, option.label])]),
    pools: new Map((pools.data ?? []).map((pool) => [pool.id, pool.name])),
    customFields: new Map((customFields.data ?? []).map((field) => [field.id, field.label])),
    stageLabels,
  });

  const openFlags = duplicates.data?.length ?? 0;
  const emptyState =
    filterCount > 0 ? (
      <FilteredEmptyState filterCount={filterCount} onClearFilters={() => changeFilters(clearFilters(search))} isInset />
    ) : (
      <EmptyState
        title="No candidates yet"
        body="People appear here when they apply through a job's link, or when you add someone by hand."
        isInset
        action={
          canEdit ? (
            <Button variant="solid" icon={UserPlus} onClick={() => void navigate({ to: '/candidates/new' })}>
              Add candidate
            </Button>
          ) : undefined
        }
      />
    );

  const filterPanel = (isPrimary: boolean): ReactNode => (
    <FilterPanel
      search={search}
      facets={facets.data}
      onChange={changeFilters}
      onClearAll={() => changeFilters(clearFilters(search))}
      skillSearch={skillSearch}
      onSkillSearch={setSkillSearch}
      canSeeSalary={canSeeSalary}
      isPrimary={isPrimary}
    />
  );

  const isFirstPaint = browse.isPending;
  const hasFailed = browse.error !== null && browse.data === undefined;
  const list =
    layout === 'table' ? (
      <CandidateTable columns={visibleColumns} rows={rows} isLoading={isFirstPaint} selection={selection} onSelectionChange={setSelection} handlers={handlers} emptyState={emptyState} />
    ) : null;

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>Candidates</h1>
          <p className={styles.lead}>Everyone who has applied to a job, or was added by hand.</p>
        </div>
        <div className={styles.actions}>
          {openFlags > 0 && (
            <Link to="/candidates/duplicates" className={styles.flagLink}>
              {openFlags === 1 ? '1 possible duplicate to review' : `${formatNumber(openFlags)} possible duplicates to review`}
            </Link>
          )}
          {canEdit && (
            <Button variant="solid" icon={UserPlus} onClick={() => void navigate({ to: '/candidates/new' })}>
              Add candidate
            </Button>
          )}
        </div>
      </header>

      <StageTiles selected={listOf(search, 'stages')} onPick={(stage) => changeFilters(pickOnlyStage(search, stage))} />

      <div className={styles.body}>
        <aside className={styles.filters} aria-label="Filters">
          {filterPanel(true)}
        </aside>

        <section className={styles.results} aria-label="Candidate list">
          <div className={layout === 'table' ? `${styles.card} ${styles.tableCard}` : styles.card}>
            <ResultsToolbar
              total={total}
              chips={chips}
              onRemoveChip={(chip) => changeFilters(chip.without)}
              filters={filters}
              onApplyFilters={(next) => changeFilters(applyFilters(search, next))}
              sort={search.sort ?? 'last_activity'}
              onSort={(sort) => go(withSort(search, sort))}
              layout={layout}
              onLayout={setLayout}
              columns={columns}
              preferences={preferences}
              filterCount={filterCount}
              onOpenFilters={() => setIsSheetOpen(true)}
            />
            {selection.mode !== 'none' && (
              <BulkBar selection={selection} onSelectionChange={setSelection} total={total ?? 0} rows={rows} filters={filters} columns={visibleColumns} canEdit={canEdit} />
            )}
            {hasFailed ? <ServerErrorState onRetry={() => void browse.refetch()} /> : list}
            {layout === 'table' && !hasFailed && rows.length > 0 && (
              <Pager page={page} pageCount={pageCount} pageSize={PAGE_SIZE} rowCount={rows.length} total={total ?? 0} onPage={(next) => go(withPage(search, next))} />
            )}
          </div>
          {layout === 'cards' && !hasFailed && (
            <>
              <CandidateCards rows={rows} isLoading={isFirstPaint} selection={selection} onSelectionChange={setSelection} handlers={handlers} emptyState={emptyState} />
              {rows.length > 0 && (
                <div className={styles.card}>
                  <Pager page={page} pageCount={pageCount} pageSize={PAGE_SIZE} rowCount={rows.length} total={total ?? 0} onPage={(next) => go(withPage(search, next))} />
                </div>
              )}
            </>
          )}
        </section>
      </div>

      <Drawer
        isOpen={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
        title="Filters"
        footer={
          <div className={styles.sheetFooter}>
            <Button variant="ghost" onClick={() => changeFilters(clearFilters(search))}>
              Clear all
            </Button>
            <Button variant="solid" onClick={() => setIsSheetOpen(false)}>
              {total === undefined ? 'Show candidates' : `Show ${formatNumber(total)} ${total === 1 ? 'candidate' : 'candidates'}`}
            </Button>
          </div>
        }
      >
        {filterPanel(false)}
      </Drawer>

      {openId !== undefined && (
        <CandidatePopup
          candidateId={openId}
          tab={search.tab ?? 'cv'}
          onTabChange={(tab) => go(withOpen(search, openId, tab))}
          nav={nav}
          onClose={closePopup}
          logKind={search.log}
        />
      )}
    </div>
  );
}

/** The first or last person of a neighbouring page, once that page (not a stand-in) has loaded. */
function pickAhead(ahead: { readonly data: BrowsePage | undefined; readonly isPlaceholderData: boolean }, at: 'first' | 'last'): CandidateBrowseRow | undefined {
  if (ahead.isPlaceholderData || ahead.data === undefined) return undefined;
  return at === 'first' ? ahead.data.rows[0] : ahead.data.rows.at(-1);
}
